import {
  Accidental,
  Modifier,
  NoteHead,
  RuntimeError,
  StaveNoteHeadBounds,
  Stem,
  Vex,
  StaveNote as VexStaveNote,
} from "vexflow4";
import {
  DEFAULT_NOTE_STYLE,
  HIGHLIGHT_NOTE_STYLE,
} from "../constants";
import { NoteMap } from "../noteMap";
import { clefBassTransform, mergeProperties, toneTransfer } from "../../utils/helpers";
import { MidiStruct, PlayInfo } from "../player";

declare var window: any;

export class StaveNote extends VexStaveNote {
  private static _midi: MidiStruct | null = null;

  static get MIDI(): MidiStruct {
    if (!this._midi) {
      this._midi = window.MIDI as unknown as MidiStruct;
    }
    return this._midi;
  }
  public multipleStems: any[] = [];
  public multipleDurations: any[] = [];
  public currentNoteIndex: number = 0;

  protected get needUseCustomRenderFlow() {
    return this.multipleDurations?.length > 0 ||
      this.multipleStems?.length > 0;
  }

  public indexOfKey = (keyName: string) => {
    return this.keys.indexOf(keyName);
  };

  clone = (newProps: any) => {
    const currentProps = {
      keys: this.keys,
      stem_direction: this.getStemDirection(),
      duration: this.duration,
      noteType: this.noteType,
      clef: this.clef,
    };

    var mergedProps = mergeProperties(currentProps, newProps);
    mergedProps.duration = mergedProps.duration + mergedProps.noteType;

    var newNote = new StaveNote(mergedProps);

    //Setting the style as the same style as the note head
    const style = this.getStyle();
    if (style) newNote.setStyle(style);

    // if (this.modifierContext != null && this.getDots() != null)
    //   newNote.addDotToAll();

    const beam = this.getBeam();
    if (beam) newNote.setBeam(beam);

    //Clone modifiers
    const modifiers = this.getModifiers();
    modifiers.forEach((modifier: Modifier) => {
      newNote.addModifier(modifier, modifier.getIndex());
    });

    return newNote;
  };

  setHighlight = (highlight: boolean) => {
    if (highlight) this.setStyle(HIGHLIGHT_NOTE_STYLE);
    else this.setStyle(DEFAULT_NOTE_STYLE);
  };

  /**
   * Return new note, from actions:
   * "UP"		=> up on stave
   * "DOWN"	=> down on stave
   * "SHARP"	=> add sharp
   * @param action
   * @returns
   */
  public change = (action: string) => {
    var newKeys = new Array();
    //for each key
    for (var k in this.keys) {
      var splitted = this.keys[k].split("/");
      splitted[0] = splitted[0].toUpperCase();
      //first char is the note
      var note = splitted[0].substring(0, 1);
      var accidentals = splitted[0].substring(1);
      var octave = parseInt(splitted[1]);

      //search index of key in NoteMap.noteMap
      let index: number = 0;
      for (let x = 0; x < NoteMap.noteMap.length; x++) {
        if (NoteMap.noteMap[x] === note) {
          index = x;
          break;
        }
      }

      //do the ACTION
      if (action === "UP")
        //increment key
        index++;
      else if (action === "DOWN")
        //descrease key
        index--;
      else if (action === "SHARP") {
        //TODO add other cases
        switch (accidentals) {
          case "":
          case null:
          case undefined:
            accidentals = "#";
            break;
          case "#":
            accidentals = "##";
            break;
        }
        alert(accidentals);
      }
      //octave changes
      if (index === NoteMap.noteMap.length) {
        index = 0;
        octave++;
      } else if (index < 0) {
        index = NoteMap.noteMap.length - 1;
        octave--;
      }
      //push new key
      note = NoteMap.noteMap[index];
      newKeys.push(note + accidentals + "/" + octave);
    }
    let dur = this.getDuration();
    let type = this.getNoteType();

    if (action === "PAUSE") {
      // if (type === "n") type = "r";
      // else if (type === "r") type = "n";
      // dur = Vex.Flow.parseNoteDurationString(this.duration);
      // var dots = "";
      // for (var i = 0; i < dur.dots; i++) dots += "d";
      // dur = dur.duration + dots + type;
    }
    //create new note with new parameters
    return this.clone({ keys: newKeys, duration: dur });
  };

  /*
   * Override the original draw flag to fix the color being drawed
   */
  drawFlag = () => {
    const ctx = this.getContext();
    if (!ctx)
      throw new RuntimeError(
        "NoCanvasContext",
        "Can't draw without a canvas context."
      );
    const glyph = this.getGlyph();
    const render_flag = this.beam === null;
    const bounds = this.getNoteHeadBounds();

    const x_begin = this.getNoteHeadBeginX();
    const x_end = this.getNoteHeadEndX();

    if (glyph.flag && render_flag) {
      const stem = this.getStem();
      let note_stem_height = 0;
      if (stem) note_stem_height = stem.getHeight();
      var flag_x, flag_y, flag_code;

      if (this.getStemDirection() === Vex.Flow.Stem.DOWN) {
        // Down stems have flags on the left.
        flag_x = x_begin + 1;
        flag_y = bounds.y_top - note_stem_height + 2;
        flag_code = glyph.code_flag_downstem;
      } else {
        // Up stems have flags on the left.
        flag_x = x_end + 1;
        flag_y = bounds.y_bottom - note_stem_height - 2;
        flag_code = glyph.code_flag_upstem;
      }

      //NOTE: The 2 lines below were added to the original code
      var ctxFillStyle = ctx.fillStyle;
      const style = this.getStyle();
      if (typeof style?.fillStyle === "string") {
        ctx.fillStyle = style.fillStyle;
      }

      // Draw the Flag
      //   Vex.Flow.renderGlyph(
      //     ctx,
      //     flag_x,
      //     flag_y,
      //     this.render_options.glyph_font_scale,
      //     flag_code
      //   );

      //NOTE: The 2 lines below were added to the original code
      //Reverting style
      ctx.fillStyle = ctxFillStyle;
    }
  };

  getPlayEvents = (playInfo: PlayInfo) => {
    //Prepare the notes to be sent
    let notes: any[] = [];

    this.keys.map((key: string) => {
      let finalKey = playInfo.clef === 'bass' ? clefBassTransform(key) : key;

      const char = finalKey.split("/")[0]
      const ocvate = finalKey.split("/")[1];
      const tone = localStorage.getItem('tone_pitch') || 'C';
      // finalKey = `${toneTransfer(char, tone)}${ocvate}`;
      const keyTransfered = `${toneTransfer(char, tone)}${ocvate}`
      notes.push(StaveNote.MIDI.keyToNote[keyTransfered]);
    });

    let keyPressTime = playInfo.defaultTime / parseInt(this.duration);

    this.getModifiers().map((modifier: any) => {
      let modValue: number = 0;
      if (modifier instanceof Accidental) {
        switch (modifier.type) {
          case "bb":
            modValue = -2;
            break;
          case "b":
            modValue = -1;
            break;
          case "n":
            modValue = 0;
            break;
          case "#":
            modValue = 1;
            break;
          case "##":
            modValue = 2;
            break;
        }

        notes = notes.map((note: number) => {
          return (note += modValue);
        });
      } else if (modifier instanceof Vex.Flow.Dot) {
        keyPressTime *= 1.5;
      }
    });

    var events = [];

    events.push({
      type: "channel",
      channel: 0,
      subtype: notes.length === 1 ? "noteOn" : "chordOn",
      noteNumber: notes.length === 1 ? notes[0] : notes,
      velocity: 127,
      queuedTime: playInfo.delay,
      note: this,
    });
    events.push({
      type: "channel",
      channel: 0,
      subtype: notes.length === 1 ? "noteOff" : "chordOff",
      noteNumber: notes.length === 1 ? notes[0] : notes,
      queuedTime: playInfo.delay + keyPressTime,
      note: this,
    });

    //increment the delay
    playInfo.delay = playInfo.delay + keyPressTime;

    return events;
  };

  draw(): void {
    if (!this.needUseCustomRenderFlow) { return super.draw(); }
    const keys = [...this.keys];
    const defaultStemDirection = this.getStemDirection();

    keys.forEach((key: string, index: number) => {
      let direction: any;

      let shouldRenderStem = true;
      let duration: string;
      try {
        duration = this.multipleDurations[index];
      } catch (e) {
        duration = '';
      }

      if (['w', '1'].includes(duration)) {
        shouldRenderStem = false;
      } else {
        shouldRenderStem = this.hasStem() && !this.beam;
      }

      try {
        direction = this.multipleStems[index] || defaultStemDirection;
      } catch (e) {
        direction = defaultStemDirection;
      }

      this.setStemDirection(direction);
      this.keys = [key];
      this.currentNoteIndex = index;

      if (this.stave)
        this.setStave(this.stave);


      /** super.draw() starts here */
      if (this.render_options.draw === false) return;

      if (this.ys.length === 0) {
        throw new RuntimeError('NoYValues', "Can't draw note without Y values.");
      }

      const ctx = this.checkContext();
      const xBegin = this.getNoteHeadBeginX();

      // Format note head x positions
      this._noteHeads.forEach((notehead) => notehead.setX(xBegin));

      if (this.stem) {
        // Format stem x positions
        const stemX = this.getStemX();
        this.stem.setNoteHeadXBounds(stemX, stemX);
      }

      // Apply the overall style -- may be contradicted by local settings:
      this.applyStyle();
      ctx.openGroup('stavenote', this.getAttribute('id'));
      this.drawLedgerLines();
      if (shouldRenderStem) this.drawStem();
      this.drawNoteHeads();
      this.drawFlag();
      ctx.closeGroup();
      this.restoreStyle();
      this.setRendered();
      /** super.draw() ends here */
    });

    this.keys = keys;
    this.setStemDirection(defaultStemDirection);
  }

  getNoteHeadBounds(): StaveNoteHeadBounds {
    if (!this.needUseCustomRenderFlow) { return super.getNoteHeadBounds(); }

    // Top and bottom Y values for stem.
    let yTop: number = +Infinity;
    let yBottom: number = -Infinity;
    let nonDisplacedX: number | undefined;
    let displacedX: number | undefined;

    let highestLine = this.checkStave().getNumLines();
    let lowestLine = 1;
    let highestDisplacedLine: number | undefined;
    let lowestDisplacedLine: number | undefined;
    let highestNonDisplacedLine = highestLine;
    let lowestNonDisplacedLine = lowestLine;

    this.noteHeads
      .filter((notehead, index) => index === this.currentNoteIndex)
      .forEach((notehead) => {
        const line: number = notehead.getLine();
        const y = notehead.getY();

        yTop = Math.min(y, yTop);
        yBottom = Math.max(y, yBottom);

        if (displacedX === undefined && notehead.isDisplaced()) {
          displacedX = notehead.getAbsoluteX();
        }

        if (nonDisplacedX === undefined && !notehead.isDisplaced()) {
          nonDisplacedX = notehead.getAbsoluteX();
        }

        highestLine = Math.max(line, highestLine);
        lowestLine = Math.min(line, lowestLine);

        if (notehead.isDisplaced()) {
          highestDisplacedLine = highestDisplacedLine === undefined ? line : Math.max(line, highestDisplacedLine);
          lowestDisplacedLine = lowestDisplacedLine === undefined ? line : Math.min(line, lowestDisplacedLine);
        } else {
          highestNonDisplacedLine = Math.max(line, highestNonDisplacedLine);
          lowestNonDisplacedLine = Math.min(line, lowestNonDisplacedLine);
        }
      }, this);

    return {
      y_top: yTop,
      y_bottom: yBottom,
      displaced_x: displacedX,
      non_displaced_x: nonDisplacedX,
      highest_line: highestLine,
      lowest_line: lowestLine,
      highest_displaced_line: highestDisplacedLine,
      lowest_displaced_line: lowestDisplacedLine,
      highest_non_displaced_line: highestNonDisplacedLine,
      lowest_non_displaced_line: lowestNonDisplacedLine,
    };
  }

  buildNoteHeads(): NoteHead[] {
    if (!this.needUseCustomRenderFlow) { return super.buildNoteHeads(); }

    this._noteHeads = [];
    const stemDirection = this.getStemDirection();
    const keys = this.getKeys();

    let lastLine = undefined;
    let lineDiff = undefined;
    let displaced = false;

    // Draw notes from bottom to top.

    // For down-stem notes, we draw from top to bottom.
    let start: number;
    let end: number;
    let step: number;
    if (stemDirection === Stem.UP) {
      start = 0;
      end = keys.length;
      step = 1;
    } else {
      start = keys.length - 1;
      end = -1;
      step = -1;
    }

    const durations = this.multipleDurations;
    const defaultDruation = this.getDuration();

    for (let i = start; i !== end; i += step) {
      let duration: string;
      try {
        duration = durations[this.sortedKeyProps[i].index] || defaultDruation;
      } catch (e) {
        duration = defaultDruation;
      }

      // Building noteheads rely on sortedKeNotes in order to calculate the displacements
      const noteProps = this.sortedKeyProps[i].keyProps;
      const line = noteProps.line;

      // Keep track of last line with a note head, so that consecutive heads
      // are correctly displaced.
      if (lastLine === undefined) {
        lastLine = line;
      } else {
        lineDiff = Math.abs(lastLine - line);
        if (lineDiff === 0 || lineDiff === 0.5) {
          displaced = !displaced;
        } else {
          displaced = false;
          this.use_default_head_x = true;
        }
      }
      lastLine = line;


      const notehead = new NoteHead({
        duration: duration,
        note_type: this.noteType,
        displaced,
        stem_direction: stemDirection,
        custom_glyph_code: noteProps.code,
        glyph_font_scale: this.render_options.glyph_font_scale,
        x_shift: noteProps.shift_right,
        stem_up_x_offset: noteProps.stem_up_x_offset,
        stem_down_x_offset: noteProps.stem_down_x_offset,
        line: noteProps.line,
      });

      this.addChildElement(notehead);
      this._noteHeads[this.sortedKeyProps[i].index] = notehead;
    }
    return this._noteHeads;
  }
}
